import { Col, Row } from 'antd';
import AccordinButton from 'components/AccordinButton';
import PictureAssignment from 'components/Image/PictureAssignment';
import ModalMenu from 'components/Modals';
import ResizeAndDraggable from 'components/ResizeAndDraggable';
import SentryWrap from 'components/Sentry';
import TextEditor from 'components/TextEditor';
import { ShareAssignmentAudioPlayer } from 'components/CustomAudioPlayer';
import AudioRecorder from 'features/AudeoRecorderNew';
import VideoRecorder from 'features/VideoRecorderNew';
import { HlsVideoPlayer } from 'components/UI';
import { FillBlanksPreview, MatchMakingPreview } from 'features/forms/assignment';
import { isQuestionEmpty } from 'features/forms/assignment/studentMatchMaking/helper';
import { find, get, map } from 'lodash';
import AssignmentPhotoCollage from 'modules/activityModule/photoCollage';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { useCurrentUser } from 'store/users/selectors';
import styled from 'styled-components';
import COLOR from 'utils/constants/color';
import { Cancel } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const AssignmentDescription = styled.span`
    color: ${COLOR.black300};
    display: block;
    font-size: 12px;
    font-weight: 400;
    line-height: 17px;
`;

const AssignmentTitle = styled.span`
    color: ${COLOR.black100};
    display: block;
    font-size: 16px;
    font-weight: 600;
    line-height: 18px;
    width: 100%;
    word-break: break-word;
    text-wrap: wrap;
`;

const ContainerQuestion = styled.div`
    display: flex;
    flex-direction: column;
    height:auto;
    margin-top:0px;
    overflow: hidden;
    width: 100%;
`;

const Content = styled.div`
    // margin-top: 24px;
     border-bottom:1px dotted;
     padding-bottom: 27px;
`;

const Detail = styled(AssignmentDescription)`
    color: ${COLOR.black600};
    display: inline-block;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    margin-top:${p => p.index == 0 ? "0px" : "24px"}
`;

const Option = styled.button`
    background-color: ${p => p.selected ? COLOR.green800 : COLOR.white};
    border: 1px dashed ${p => p.selected ? COLOR.green300 : COLOR.black700};
    border-radius: 3px;
    color: ${p => p.selected ? COLOR.white : COLOR.black400};
    cursor: pointer;
    font-size: 12px;
    line-height: 16px;
    padding: 4px 6px;
    outline: none;
    text-align: left;
    transition: all 300ms ease;
    width: 100%;

    &:hover {
        box-shadow: ${p => p.selected ? 'none' : '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)'};
        color: '${p => p.selected ? COLOR.green100 : COLOR.black200}';
    }
`;

const Question = styled(AssignmentTitle)`
    font-size: 16px;
    line-height: 22px;
    ol, ul {
        padding-left: 30px;
        margin: 10px 0;
    }
    
    ol {
        list-style-type: decimal;
    }
    
    ul {
        list-style-type: disc;
    }
    
    li {
        margin: 5px 0;
        line-height: 1.6;
    }
    
    ol ol {
        list-style-type: lower-alpha;
        padding-left: 30px;
        margin-top: 5px;
        margin-bottom: 5px;
    }
    
    ol ol ol {
        list-style-type: lower-roman;
        padding-left: 30px;
    }
    
    ul ul {
        list-style-type: circle;
        padding-left: 30px;
        margin-top: 5px;
        margin-bottom: 5px;
    }
    
    ul ul ul {
        list-style-type: square;
        padding-left: 30px;
    }
    
    ol ul,
    ul ol {
        margin-top: 5px;
        margin-bottom: 5px;
        padding-left: 30px;
    }
    
    p {
        margin: 10px 0;
        line-height: 1.6;
    }
`;

const QuestionContainer = styled(ContainerQuestion)`
    margin-top: 0px;
    position: relative;
`;

const RelativeContainer = styled.div`
    position: relative;
    margin-top:${p => p.marginTop ? p.marginTop : "0px"}
    width: 100%;
    margin-bottom:20px;
`;

const Section = styled.div`
    width: 100%;
`;
const Images = styled.img`
    background-color: ${COLOR.white};
    box-shadow: 0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23);
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    border-radius:8px;
    height: 100%;
    width: auto;
    max-width:250px;
    max-height: 250px;
`;

const ImagesScrollContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    gap: 15px;
    padding: 10px 0;
    margin-top: 25px;
    width: 100%;
    min-height: 200px;
    max-height: 400px;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
        width: 8px;
        -webkit-appearance: none;
        display: block !important;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
        -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.1);
    }
    
    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 10px;
        -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
        
        &:hover {
            background: #555;
        }
    }
    &::-webkit-scrollbar:vertical {
        width: 8px;
    }
    
`;

const ImageItem = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

const TopBarRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 12px;
    margin-top: 6px;
`;

const Close = styled.span`
flex-direction:row;
padding-top:5px;
padding-bottom:5px;
padding-right:10px;
background-color: ${p => p.selected ? COLOR.green800 : COLOR.white};
border: 1px dashed ${p => p.selected ? COLOR.green300 : COLOR.black700};
border-radius: 3px;
color: ${p => p.selected ? COLOR.white : COLOR.black400};
cursor: pointer;
padding-left:10px;
width:150px;
font-size: 14px;
`;

const ShareButton = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background-color: ${COLOR.green800};
    color: ${COLOR.white};
    border-radius: 3px;
    cursor: pointer;
    font-size: 14px;
    &:hover {
        opacity: 0.9;
    }
`;

const CloseWidgetButton = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    color: ${COLOR.white};
    border-radius: 3px;
    cursor: pointer;
    font-size: 14px;
    margin-left: auto;
    &:hover {
        opacity: 0.9;
        background: rgba(255, 255, 255, 0.15);
    }
`;

const Wrapper = styled.div`
    height: 100%;
    // max-width: 480px;
    padding:0px;
    width: 100%;
    min-height:480px;
   
`;

const InnerWrapper = styled.div`
    // max-height: 120vh;
    // max-width: 480px;
    padding: 0px;
    width: 100%;
    overflow-y:auto;
    scroll-behavior: smooth;
    padding-bottom:5px;
    // border-bottom:1px dotted;
    overflow-y: auto;

   @media screen and (min-width: 1100px) and (max-width:1150px){
     max-height: 542px;
   }
     @media screen and (min-width: 1150px) and (max-width:1200px){
     max-height: 615px;
   }

    @media screen and (min-width: 1250px) and (max-width:1340px){
     max-height: 745px;
   }  
   
`;

const AssignmentPreviewTop = styled.div`
    overflow-y:auto;
    max-height:${p => p.wrapperHeight > p.height ? `${p.wrapperHeight-100}px` : `${p.height-20}px`}
`;

const getDefaultDimensions = () => {
    if (typeof window === 'undefined') {
        return { x: 20, y: 20, width: 500, height: 600 };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const minMargin = 20;
    let widgetWidth = 500;
    let widgetHeight = 600;

    if (width < 520) {
        widgetWidth = Math.max(400, width - minMargin * 2);
        widgetHeight = Math.max(400, height - minMargin * 2);
    } else if (width < 768) {
        widgetWidth = Math.max(400, Math.min(width - minMargin * 2, 500));
        widgetHeight = Math.max(400, Math.min(height - minMargin * 2, 600));
    }

    const x = Math.max(minMargin, (width - widgetWidth) / 2);
    const y = Math.max(minMargin, (height - widgetHeight) / 2);

    return {
        x: Math.min(x, width - widgetWidth - minMargin),
        y: Math.min(y, height - widgetHeight - minMargin),
        width: widgetWidth,
        height: widgetHeight,
    };
};

const getMinWidth = () => {
    if (typeof window === 'undefined') return 500;
    return window.innerWidth < 768 ? 270 : 500;
};

const getMinHeight = () => {
    if (typeof window === 'undefined') return 500;
    return window.innerWidth < 768 ? 300 : 400;
};

//  ----------------------------------------------------------------------------
//  Component AssignmentPreview
//  ----------------------------------------------------------------------------

const ShareAssignmentWidget = ({ sharableWidget = [], currentClass }) => {
    const TEACHER_ACTIVE_TIMEOUT_MS = 30000;
    const [teacherActiveCheckTick, setTeacherActiveCheckTick] = useState(Date.now());
    const [imageModal, setImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selections, setSelections] = useState("");
    const [showAccordin, setShowAccordin] = useState(true);
    const [defaultDimensions] = useState(getDefaultDimensions());
    const [dimensions, setDimensions] = useState({ 
        width: defaultDimensions.width, 
        height: defaultDimensions.height 
    });
    const innerRef = useRef(null);
    const wrapperRef = useRef(null);
    const [wrapperHeight, setWrapperHeight] = useState(0);
    const audioPlayersRef = useRef(new Map());
    const mediaRegistryRef = useRef(new Set());
    const registerMediaElement = (mediaEl) => {
        if (!mediaEl) return;
        mediaRegistryRef.current.add(mediaEl);
    };
    const handleMediaPlay = (playedEl) => {
        mediaRegistryRef.current.forEach((media) => {
            if (!media) return;
            if (media === playedEl) return;
            if (!media.paused && !media.ended) {
                try {
                    media.pause();
                } catch (e) { }
            }
        });
    };
    const lastSyncedDataIdRef = useRef(null);
    const currentUser = useCurrentUser();

    const filterShareAssignment = find(sharableWidget, (obj) => obj?.widget_type === 'share_assignment');
    const widgetData = filterShareAssignment?.data || {};

    const isOpen = useMemo(() => {
        return !filterShareAssignment?.id || currentUser?.role !== 'student';
    }, [filterShareAssignment, currentUser]);

    const data = useMemo(() => {
        const questionsRaw = widgetData?.questions;
        const questions = typeof questionsRaw === 'string' ? (() => { try { return JSON.parse(questionsRaw) || []; } catch { return []; } })() : (Array.isArray(questionsRaw) ? questionsRaw : []);
        const answers = typeof widgetData?.answers === 'string' ? (() => { try { return JSON.parse(widgetData?.answers) || []; } catch { return []; } })() : (Array.isArray(widgetData?.answers) ? widgetData?.answers : []);
        return {
            id: filterShareAssignment?.id,
            displayName: widgetData?.displayName ?? '',
            description: widgetData?.description ?? '',
            questions,
            answers,
        };
    }, [filterShareAssignment, widgetData, isOpen]);

    

    // const answersJson = useMemo(() => JSON.stringify(data?.answers ?? []), [data?.answers]);

    // useEffect(() => {
    //     const fromData = data?.answers;
    //     if (!Array.isArray(fromData) || fromData.length === 0) return;
    //     lastSyncedDataIdRef.current = data?.id;
    //     setAnswers(fromData.map((a) => (typeof a === 'object' && a !== null && 'answer' in a ? a : { answer: a })));
    // }, [data?.id, answersJson]);

    useEffect(() => {
        if (innerRef.current) {
            innerRef.current.scrollTop = 0;
        }
    }, [data?.id]);

   

    useEffect(() => {
        const element = wrapperRef.current;
        if (!element) return;
        const nextHeight = element.clientHeight;
        setWrapperHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    }, [dimensions, showAccordin]);

    useEffect(() => {
        const onMediaPlay = (event) => {
            const target = event?.target;
            const medias = document.querySelectorAll("audio, video");

            if (target instanceof HTMLMediaElement) {
                mediaRegistryRef.current.add(target);

                mediaRegistryRef.current.forEach((media) => {
                    if (media === target) return;
                    if (!media.paused && !media.ended) {
                        try {
                            media.pause();
                        } catch (e) { }
                    }
                });

                medias.forEach((media) => {
                    if (media === target) return;
                    if (!media.paused && !media.ended) {
                        try {
                            media.pause();
                        } catch (e) { }
                    }
                });
                return;
            }

            const active = Array.from(medias).find((m) => !m.paused && !m.ended);
            const keep = active || null;
            medias.forEach((media) => {
                if (keep && media === keep) return;
                if (!media.paused && !media.ended) {
                    try {
                        media.pause();
                    } catch (e) { }
                }
            });
        };

        document.addEventListener("play", onMediaPlay, true);
        return () => document.removeEventListener("play", onMediaPlay, true);
    }, []);




    const pauseAllOtherAudioPlayers = (currentPlayerRef) => {
        audioPlayersRef.current.forEach((playerRef) => {
            if (playerRef === currentPlayerRef || !playerRef?.current) return;
            try {
                const el = playerRef.current;
                if (el instanceof HTMLMediaElement) {
                    if (!el.paused) el.pause();
                    return;
                }
                if (typeof el.getInternalPlayer === 'function') {
                    const internalPlayer = el.getInternalPlayer();
                    if (internalPlayer && !internalPlayer.paused) {
                        internalPlayer.pause();
                    }
                }
            } catch (error) {
            }
        });
    };

    const audioMultipleChoiceGroups = useMemo(() => {
        const questions = get(data, "questions", []);
        const groups = {};
        questions.forEach((q, idx) => {
            if (q.type === 'audioMultipleChoice' && q.groupKey) {
                if (!groups[q.groupKey]) {
                    groups[q.groupKey] = {
                        firstIndex: idx,
                        questions: []
                    };
                }
                groups[q.groupKey].questions.push(q);
            }
        });
        return groups;
    }, [data?.questions]);

    const audioEssayGroups = useMemo(() => {
        const questions = get(data, "questions", []);
        const groups = {};
        questions.forEach((q, idx) => {
            if (q.type === 'audioEssay' && q.groupKey) {
                if (!groups[q.groupKey]) {
                    groups[q.groupKey] = {
                        firstIndex: idx,
                        questions: []
                    };
                }
                groups[q.groupKey].questions.push({ ...q, originalIndex: idx });
            }
        });
        return groups;
    }, [data?.questions]);

    const getPictureAnswer = (raw) => {
        if (raw == null) return { source: null, additionalPictures: [] };
        if (typeof raw === 'string') return { source: raw, additionalPictures: [] };
        if (Array.isArray(raw)) return { source: null, additionalPictures: [] };
        if (typeof raw === 'object' && raw.answer != null) {
            const inner = getPictureAnswer(raw.answer);
            if (inner.additionalPictures.length > 0 || inner.source) return inner;
        }
        if (typeof raw === 'object') {
            const source = raw.url ?? raw.source ?? null;
            const numericKeys = Object.keys(raw)
                .filter(k => k !== 'answer' && k !== 'additionalPictures' && k !== 'url' && k !== 'source')
                .map(Number)
                .filter(n => !Number.isNaN(n))
                .sort((a, b) => a - b);
            const urlFromSpread = numericKeys.length > 0 ? numericKeys.map(k => raw[k]).join('') : null;
            const resolvedSource = source || urlFromSpread || null;
            const additionalPictures = Array.isArray(raw.additionalPictures) ? raw.additionalPictures : [];
            return { source: resolvedSource, additionalPictures };
        }
        return { source: null, additionalPictures: [] };
    };


    const renderAudioMultipleChoice = (question, index) => {
        const group = audioMultipleChoiceGroups[question.groupKey];
        if (!group || group.firstIndex !== index) return null;
        return (
            <RelativeContainer key={question.groupKey}>
                <QuestionContainer>
                    {/* <Detail style={{ marginTop: '5px', marginBottom: '5px' }} index={index}>
                        Question {index + 1} of {data?.questions?.length}
                    </Detail> */}
                    {/* {question.audio && (
                        <TranscriptWithAudio audioUrl={question.audio} title={question.title}>
                            <ReactPlayer
                                url={question.audio}
                                height="40px"
                                width="100%"
                                controls
                                playsinline
                                style={{ marginBottom: '8px' }}
                                config={{ file: { forceAudio: true, attributes: { controlsList: "nodownload" } } }}
                            />
                        </TranscriptWithAudio>
                    )} */}
                    <Content>
                        {group.questions.map((q, qIndex) => {
                            const displayIndex = group.firstIndex + qIndex;
                            const groupedAnswer = data?.answers?.[displayIndex];
                            return (
                            <div key={q.id || qIndex} style={{ marginBottom: '16px' }}>
                                <Detail style={{ marginTop: '5px', marginBottom: '5px' }} index={qIndex}>
                                    Question {group.firstIndex + qIndex + 1} of {data?.questions?.length}
                                </Detail>
                                {q.audio && (
                                    <ShareAssignmentAudioPlayer
                                        url={q.audio}
                                        audioKey={`audioMultipleChoice-group-${question.groupKey}-${qIndex}`}
                                        title={q.title}
                                        audioPlayersRef={audioPlayersRef}
                                        pauseAllOtherAudioPlayers={pauseAllOtherAudioPlayers}
                                    />
                                )}
                                {!isQuestionEmpty(q.question) && (
                                    <Question
                                        style={{ fontSize: '14px', marginBottom: '8px' }}
                                        dangerouslySetInnerHTML={{ __html: q.question || '' }}
                                    />
                                )}
                                <Row gutter={[0, 0]}>
                                    {map(get(q, "options", []), (option, i) => {
                                        return (
                                            <Row key={i} style={{ marginBottom: '8px', width: '100%' }}>
                                                <Option
                                                    selected={groupedAnswer?.answer === i}
                                                    onClick={() => { }}
                                                >
                                                    {option}
                                                </Option>
                                            </Row>
                                        );
                                    })}
                                </Row>
                            </div>
                        )})}
                    </Content>
                </QuestionContainer>
            </RelativeContainer>
        );
    }

    const location = useLocation();

    const isSamePageFromSharedAssignment = useMemo(() => {
        const chapter = filterShareAssignment?.data?.chapter;
        const page = filterShareAssignment?.data?.page;
        const from = filterShareAssignment?.data?.from;

        if (!chapter || !page || typeof window === 'undefined') {
            return false;
        }


        const params = new URLSearchParams(window.location.search || '');
        const currentChapter = params.get('chapter') || params.get('id');


        return String(currentChapter) === String(chapter);
    }, [filterShareAssignment?.data?.chapter, filterShareAssignment?.data?.page, filterShareAssignment?.data?.from, location?.search]);

    const isTeacherActiveForSharedAssignment = useMemo(() => {
        if (currentUser?.role !== 'student') return true;
        if (!filterShareAssignment) return false;
    

        const lastActiveAt = Number(filterShareAssignment?.last_active_at || 0);
    
        if (lastActiveAt > 0) {
            const isTimeoutPassed = Date.now() - lastActiveAt > TEACHER_ACTIVE_TIMEOUT_MS;
            return !isTimeoutPassed;
        }
    
        return true;
    }, [currentUser?.role, filterShareAssignment, teacherActiveCheckTick]);

    useEffect(() => {
        if (currentUser?.role !== 'student') return undefined;
        const intervalId = setInterval(() => {
            setTeacherActiveCheckTick(Date.now());
        }, TEACHER_ACTIVE_TIMEOUT_MS);
        return () => clearInterval(intervalId);
    }, [currentUser?.role]);

    if (isOpen || !isSamePageFromSharedAssignment || !isTeacherActiveForSharedAssignment) {
        return null;
    }


    return (
        <SentryWrap>
            <Container className="allCheckListSEction wigetsContainer cursor-default">
                <div className="mainCheckList  h-full">
                    <ResizeAndDraggable
                        // defaultValue={{ x: 120, y: 80, width: 420, height: 560 }}
                        minWidth={getMinWidth()}
                        minHeight={getMinHeight()}
                        maxWidth="98vw"
                        showAccordin={showAccordin}
                        resize={(data) => setDimensions(data)}
                        // resize={(layout) => setDimensions(layout)}
                        // resizeEnd={(data) => setDimensions(data)}

                    >
                        <div
                            className="checkListTopSection addWidgetSpc"
                            style={{
                                position: 'relative',
                                boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px',
                                height: showAccordin ? '100%' : '44px',
                                width: '100%',
                                padding: 0,
                                overflow: 'hidden',
                            }}
                        >
                            <Wrapper ref={wrapperRef} className='wraper-assignPreview-cstm'>
                                <Container>
                                    <div className={`checkListFirstTxt widgetFooterGradientOrange bg-blue-10 h-44px pt-17px pb-14px ${showAccordin ? 'br-topLeft-topRight-20px' : 'br-10px'}`}>
                                        <AccordinButton
                                            open={showAccordin}
                                            onClose={() => setShowAccordin((prev) => !prev)}
                                            shareableWidget={sharableWidget}
                                        />
                                        <span className="text-white text-ft12-700">Assignment</span>
                                        {/* <CloseWidgetButton onClick={() => setShowAccordin(false)} title="Close">
                                            <CloseOutlined />
                                        </CloseWidgetButton> */}
                                    </div>

                                    {showAccordin && (
                                        <>
                                            <Section style={{ padding: '16px' }}>
                                                <Row align="middle" gutter={[0, 16]}>
                                                    <Col span={24}>
                                                        <AssignmentTitle>{data?.displayName}</AssignmentTitle>
                                                    </Col>
                                                </Row>
                                                {data?.description && data?.description.length > 0 && (
                                                    <Row gutter={[0, 24]}>
                                                        <Col span={24}>
                                                            <AssignmentDescription>{data?.description}</AssignmentDescription>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </Section>
                                            <AssignmentPreviewTop wrapperHeight={wrapperHeight} className='assignPreviewHght' style={{ padding: '16px' }} height={dimensions?.height - 100} data-no-drag={true}>
                                                <InnerWrapper ref={innerRef} className='previewInnerCstm'>
                                                    {map(data?.questions, (question, index) => {
                                                        const ans = data?.answers[index];
                                                        if (question.type === 'audioMultipleChoice' && question.groupKey) {
                                                            return renderAudioMultipleChoice(question, index);
                                                        }
                                                        if (question.type === 'audioEssay' && question.groupKey) {
                                                            const group = audioEssayGroups[question.groupKey];
                                                            if (!group || group.firstIndex !== index) return null;
                                                            return (
                                                                <RelativeContainer key={question.groupKey} className="relative-container">
                                                                    <QuestionContainer>
                                                                        {group.questions.map((q, qIndex) => {
                                                                            const displayIndex = q.originalIndex !== undefined ? q.originalIndex : group.firstIndex + qIndex;
                                                                            const groupedAnswer = data?.answers?.[displayIndex];
                                                                            return (
                                                                                <div key={q.id || qIndex} style={{ marginTop: qIndex > 0 ? '24px' : '0px', borderTop: qIndex > 0 ? '1px solid #ddd' : 'none', paddingTop: qIndex > 0 ? '16px' : '0px' }}>
                                                                                    <Detail style={{ marginTop: '5px', marginBottom: '5px' }} index={displayIndex}>
                                                                                        Question {displayIndex + 1} of {data?.questions.length}
                                                                                    </Detail>
                                                                                    {q.audio && (
                                                                                        <ShareAssignmentAudioPlayer
                                                                                            url={q.audio}
                                                                                            audioKey={`audioEssay-group-${q.groupKey}-${qIndex}`}
                                                                                            title={q.title}
                                                                                            audioPlayersRef={audioPlayersRef}
                                                                                            pauseAllOtherAudioPlayers={pauseAllOtherAudioPlayers}
                                                                                            style={{ marginTop: '8px' }}
                                                                                        />
                                                                                    )}
                                                                                    <Question dangerouslySetInnerHTML={{ __html: q.question || '' }} />
                                                                                    <Row gutter={[0, 24]} style={{ marginTop: '16px' }}>
                                                                                        <Col span={24}>
                                                                                            <TextEditor
                                                                                                value={groupedAnswer?.answer ?? ''}
                                                                                                autoFocus={false}
                                                                                                disabled={true}
                                                                                            />
                                                                                        </Col>
                                                                                    </Row>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </QuestionContainer>
                                                                </RelativeContainer>
                                                            );
                                                        }

                                                        return (
                                                            <RelativeContainer key={index}>
                                                                <QuestionContainer key={question.id} >
                                                                    {/* {(question.type === "filling" || question.type === 'fillingImage') ? "" : ( */}
                                                                    <>
                                                                        <Detail style={{ marginTop: '5px', marginBottom: '5px' }} index={index}>Question {index + 1} of {data?.questions.length}</Detail>

                                                                    </>
                                                                    {/* )} */}
                                                                    {question.standard_story && (
                                                                        <Question style={{ fontSize: '13px', marginTop: '5px', marginBottom: '5px' }}>Standard Story: {question.standard_story}</Question>
                                                                    )}
                                                                    {!isQuestionEmpty(question.question) && (
                                                                        <Question dangerouslySetInnerHTML={{ __html: question.question || '' }} />
                                                                    )}
                                                                    {question.type === "picture" && (
                                                                        <>
                                                                            {(() => {
                                                                                const allImages = [];
                                                                                if (question.pictures) {
                                                                                    if (Array.isArray(question.pictures)) {
                                                                                        allImages.push(...question.pictures);
                                                                                    } else {
                                                                                        allImages.push(question.pictures);
                                                                                    }
                                                                                }
                                                                                if (question.additionalPictures && Array.isArray(question.additionalPictures)) {
                                                                                    allImages.push(...question.additionalPictures);
                                                                                }

                                                                                if (allImages.length === 0) {
                                                                                    return null;
                                                                                }

                                                                                if (allImages.length === 1) {
                                                                                    return (
                                                                                        <div style={{
                                                                                            display: 'flex',
                                                                                            justifyContent: 'center',
                                                                                            alignItems: 'center',
                                                                                            marginTop: 25
                                                                                        }}>
                                                                                            <Images
                                                                                                className='cursor-zoomIn gradeBook-answer-image-width-height'
                                                                                                src={allImages[0]}
                                                                                                onClick={() => setImageModal(true)}
                                                                                            />
                                                                                            <ModalMenu
                                                                                                type="imageModal"
                                                                                                image={allImages[0]}
                                                                                                open={imageModal}
                                                                                                close={() => setImageModal(false)}
                                                                                            />
                                                                                        </div>
                                                                                    );
                                                                                }

                                                                                return (
                                                                                    <ImagesScrollContainer className="assignQuesPrvwCstm">
                                                                                        {allImages.map((imageUrl, imgIndex) => (
                                                                                            <ImageItem key={imgIndex}>
                                                                                                <Images
                                                                                                    className='cursor-zoomIn gradeBook-answer-image-width-height'
                                                                                                    src={imageUrl}
                                                                                                    onClick={() => {
                                                                                                        setSelectedImage(imageUrl);
                                                                                                        setImageModal(true);
                                                                                                    }}
                                                                                                />
                                                                                            </ImageItem>
                                                                                        ))}
                                                                                        <ModalMenu
                                                                                            type="imageModal"
                                                                                            image={selectedImage || allImages[0]}
                                                                                            open={imageModal}
                                                                                            close={() => {
                                                                                                setImageModal(false);
                                                                                                setSelectedImage(null);
                                                                                            }}
                                                                                        />
                                                                                    </ImagesScrollContainer>
                                                                                );
                                                                            })()}
                                                                        </>
                                                                    )}
                                                                    {question.type === "audioToImage" && (
                                                                        <div style={{
                                                                            display: 'flex',
                                                                            justifyContent: 'center',
                                                                            alignItems: 'center',
                                                                            marginTop: 25
                                                                        }}>
                                                                            <Images className='cursor-zoomIn gradeBook-answer-image-width-height' src={question.image}
                                                                                onClick={() => setImageModal(true)}
                                                                            />
                                                                            <ModalMenu type="imageModal" image={question.image} open={imageModal} close={() => setImageModal(false)} />
                                                                        </div>
                                                                    )}
                                                                    <Content style={{ marginTop: (question.type === "filling" || question.type === 'fillingImage') && "0px" }}>
                                                                        {question.type === 'email' && (
                                                                            <Row gutter={[0, 24]}>
                                                                                <Col span={24}>
                                                                                    <TextEditor
                                                                                        value={ans?.answer ?? ''}
                                                                                        autoFocus={false}
                                                                                        disabled={true}
                                                                                        from="shareAssignment"
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                        )}
                                                                        {question.type === 'essay' && (
                                                                            <Row gutter={[0, 24]}>
                                                                                <Col span={24}>
                                                                                    <TextEditor
                                                                                        value={ans?.answer ?? ''}
                                                                                        autoFocus={false}
                                                                                        disabled={true}
                                                                                        from="shareAssignment"
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                        )}
                                                                        {(question.type === 'multipleChoice' || question.type === 'multipleResponse') && (
                                                                            <>
                                                                                {map(get(question, "options", []), (option, i) => {
                                                                                    const isMultiple = question.type === 'multipleResponse';
                                                                                    const isSelected = isMultiple
                                                                                        ? (ans?.answer || []).includes(i)
                                                                                        : ans?.answer === i;
                                                                                    return (
                                                                                        <Row key={i} style={{ marginBottom: '8px' }}>
                                                                                            <Option
                                                                                                selected={isSelected}
                                                                                                onClick={() => { }}
                                                                                            >
                                                                                                {option}
                                                                                            </Option>
                                                                                        </Row>
                                                                                    );
                                                                                })}
                                                                            </>
                                                                        )}
                                                                        {(question.type === 'audio' || question.type === 'audioEssay' || question.type == 'audioTrueFalse' || question.type == 'audioMultipleChoice' || question.type == 'audioToImage') && (
                                                                            <>
                                                                                {question.audio && (
                                                                                    <ShareAssignmentAudioPlayer
                                                                                        url={question.audio}
                                                                                        audioKey={`${question.type}-${index}`}
                                                                                        title={question.title}
                                                                                        audioPlayersRef={audioPlayersRef}
                                                                                        pauseAllOtherAudioPlayers={pauseAllOtherAudioPlayers}
                                                                                    />
                                                                                )}
                                                                                {question.type === 'audio' && (
                                                                                    <>
                                                                                        {ans?.answer ?
                                                                                            <ShareAssignmentAudioPlayer
                                                                                                url={ans?.answer}
                                                                                                audioKey={`${question.type}-${index}-answer`}
                                                                                                audioPlayersRef={audioPlayersRef}
                                                                                                pauseAllOtherAudioPlayers={pauseAllOtherAudioPlayers}
                                                                                                withTranscript={false}
                                                                                            />
                                                                                            : <AudioRecorder
                                                                                                maxTime={question?.duration || 20}
                                                                                                handleAudio={() => { }}
                                                                                                index={index}
                                                                                                currentSubmission={{}}
                                                                                                refresh={() => { }}
                                                                                                isPreview={true}
                                                                                                onRegisterMediaElement={registerMediaElement}
                                                                                                onMediaPlay={handleMediaPlay}
                                                                                            />}

                                                                                    </>
                                                                                )}
                                                                                {question.type === 'audioEssay' && !question.groupKey && (
                                                                                    <Row gutter={[0, 24]}>
                                                                                        <Col span={24}>
                                                                                            <TextEditor
                                                                                                value={ans?.answer ?? ''}
                                                                                                autoFocus={false}
                                                                                                disabled={true}
                                                                                                from="shareAssignment"
                                                                                            />
                                                                                        </Col>
                                                                                    </Row>
                                                                                )}
                                                                                {question.type === 'audioToImage' && (
                                                                                    (() => {
                                                                                        const { source: audioImageSource, additionalPictures: audioImageAdditional } = getPictureAnswer(ans?.answer);
                                                                                        return (
                                                                                            <PictureAssignment
                                                                                                disabled={true}
                                                                                                containerClassName={'assignment-file-preview assignment-ebook'}
                                                                                                imageClassName={'assignment-image'}
                                                                                                path={"ShareAssignment"}
                                                                                                source={audioImageSource}
                                                                                                additionalImages={audioImageAdditional}
                                                                                                from="shareAssignment"
                                                                                            />
                                                                                        );
                                                                                    })()
                                                                                )}
                                                                                {(question.type === 'audioTrueFalse' || question.type == 'audioMultipleChoice') && (
                                                                                    <Row gutter={[0, 24]}>
                                                                                        {map(get(question, "options", []), (option, i) => {
                                                                                            const isSelected = ans?.answer === i;
                                                                                            return (
                                                                                                <Row key={i} style={{ marginBottom: '8px' }}>
                                                                                                    <Option
                                                                                                        selected={isSelected}
                                                                                                        onClick={() => { }}
                                                                                                    >
                                                                                                        {option}
                                                                                                    </Option>
                                                                                                </Row>
                                                                                            );
                                                                                        })}
                                                                                    </Row>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                        {question.type === 'video' && (
                                                                            <>
                                                                                {question?.video ? (
                                                                                    <HlsVideoPlayer
                                                                                        src={question?.video || ""}
                                                                                        style={{ marginBottom: '8px' }}
                                                                                    />
                                                                                ) : null}

                                                                                {ans?.answer ? (
                                                                                    <HlsVideoPlayer
                                                                                        src={ans?.answer}
                                                                                        style={{ marginBottom: '8px' }}
                                                                                    />
                                                                                ) : (
                                                                                    <VideoRecorder
                                                                                        maxTime={question?.duration || 20}
                                                                                        handleVideo={() => { }}
                                                                                        index={index}
                                                                                        isPreview={true}
                                                                                        onRegisterMediaElement={registerMediaElement}
                                                                                        onMediaPlay={handleMediaPlay}
                                                                                    />
                                                                                )}

                                                                            </>
                                                                        )}
                                                                        {question.type === 'twoMinAudio' && (
                                                                            <>
                                                                                <AudioRecorder
                                                                                    maxTime={120}
                                                                                    handleAudio={() => { }}
                                                                                    refresh={() => { }}
                                                                                    isPreview={true}
                                                                                    onRegisterMediaElement={registerMediaElement}
                                                                                    onMediaPlay={handleMediaPlay}
                                                                                />
                                                                            </>
                                                                        )}
                                                                        {question.type === 'trueFalse' && (
                                                                            <>
                                                                                {map(get(question, "options", []), (option, i) => {
                                                                                    const isSelected = ans?.answer === i;
                                                                                    return (
                                                                                        <Row key={i} style={{ marginBottom: '8px' }}>
                                                                                            <Option
                                                                                                selected={isSelected}
                                                                                                onClick={() => { }}
                                                                                            >
                                                                                                {option}
                                                                                            </Option>
                                                                                        </Row>
                                                                                    );
                                                                                })}
                                                                            </>
                                                                        )}
                                                                        {question.type === 'picture' && (
                                                                            <>
                                                                                {question?.categoryType === "Image" ? (
                                                                                    (() => {
                                                                                        const { source: pictureSource, additionalPictures: pictureAdditional } = getPictureAnswer(ans?.answer);
                                                                                        return (
                                                                                            <PictureAssignment
                                                                                                containerClassName={"assignment-file-preview assignment-ebook"}
                                                                                                disabled={true}
                                                                                                imageClassName="assignment-image"
                                                                                                path={"AssignmentPreview"}
                                                                                                source={pictureSource}
                                                                                                additionalImages={pictureAdditional}
                                                                                                from="shareAssignment"
                                                                                            />
                                                                                        );
                                                                                    })()
                                                                                ) : question?.categoryType === "ImageAndText" ? (
                                                                                    <>
                                                                                        <PictureAssignment
                                                                                            containerClassName={"assignment-file-preview assignment-ebook"}
                                                                                            disabled={true}
                                                                                            imageClassName="assignment-image"
                                                                                            source={ans?.answer}
                                                                                            from="shareAssignment"
                                                                                        />
                                                                                        <Row gutter={[0, 24]}>
                                                                                            <Col span={24}>
                                                                                                <TextEditor
                                                                                                    value={ans?.answer ?? ''}
                                                                                                    autoFocus={false}
                                                                                                    disabled={true}
                                                                                                    from="shareAssignment"
                                                                                                />
                                                                                            </Col>
                                                                                        </Row>
                                                                                    </>
                                                                                )
                                                                                    : question?.categoryType === "Text" ?
                                                                                        <Row gutter={[0, 24]}>
                                                                                            <Col span={24}>
                                                                                                <TextEditor
                                                                                                    value={ans?.answer ?? ''}
                                                                                                    autoFocus={false}
                                                                                                    disabled={true}
                                                                                                    from="shareAssignment"
                                                                                                />
                                                                                            </Col>
                                                                                        </Row>
                                                                                        : null}

                                                                            </>
                                                                        )}


                                                                        {(question.type === "filling" || question.type === 'fillingImage') && (
                                                                            <>
                                                                                {map(get(question, "options", [""]), (option, optId) => {
                                                                                    const optionText = option?.value ?? option;
                                                                                    const teacherSelected = ans?.answer?.selected_answer?.[optId] || [];
                                                                                    const displayAnswer = teacherSelected.map(item => ({
                                                                                        text: item.answer ?? item.text,
                                                                                        answer: item.answer ?? item.text,
                                                                                    }));
                                                                                    return (
                                                                                        <div key={optId} className='mb-10px'>
                                                                                            <Detail index={index}>Answer Key {optId + 1} of {question?.options?.length}</Detail>
                                                                                            <FillBlanksPreview
                                                                                                isAssignmentReview={true}
                                                                                                text={optionText}
                                                                                                highlights={question?.highlightWords && question.highlightWords[optId] || []}
                                                                                                selected_answer={displayAnswer}
                                                                                            />
                                                                                            {(question.type === 'fillingImage' || (question.fillingType === "Image" && question?.filling_image?.[optId]?.url)) && (
                                                                                                <>
                                                                                                    <ModalMenu type="imageModal" image={question?.filling_image?.[optId]?.url} open={imageModal} close={() => setImageModal(false)} />
                                                                                                    <img
                                                                                                        alt={`filling ${optId}`}
                                                                                                        src={question?.filling_image?.[optId]?.url}
                                                                                                        draggable={false}
                                                                                                        style={{
                                                                                                            width: "100px",
                                                                                                            height: "100px",
                                                                                                            borderRadius: "8px",
                                                                                                            marginTop: "10px",
                                                                                                            cursor: "pointer"
                                                                                                        }}
                                                                                                    />
                                                                                                </>
                                                                                            )}
                                                                                            {question.fillingType === 'Audio' && question?.filling_image?.[optId]?.url && (
                                                                                                <ShareAssignmentAudioPlayer
                                                                                                    url={question?.filling_image?.[optId]?.url}
                                                                                                    audioKey={`filling-${index}-${optId}`}
                                                                                                    title={question.title}
                                                                                                    audioPlayersRef={audioPlayersRef}
                                                                                                    pauseAllOtherAudioPlayers={pauseAllOtherAudioPlayers}
                                                                                                    style={{ marginTop: '8px' }}
                                                                                                />
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </>
                                                                        )}
                                                                        {question.type === "match" && (
                                                                            <>
                                                                                <MatchMakingPreview
                                                                                    question={question}
                                                                                    value={ans?.answer}
                                                                                    from="shareAssignment"
                                                                                    onRegisterMediaElement={registerMediaElement}
                                                                                    onMediaPlay={handleMediaPlay}
                                                                                />
                                                                            </>
                                                                        )}
                                                                        {question.type === "photoCollage" && (
                                                                            <>
                                                                                <AssignmentPhotoCollage
                                                                                    states={{
                                                                                        index,
                                                                                        question,
                                                                                        isPreview: true,
                                                                                        selectData: true,
                                                                                        collageLength: question?.collageLength,
                                                                                        frameCollage: ans?.answer?.collage ?? question?.collageFrame,
                                                                                        from: "shareAssignment"
                                                                                    }}
                                                                                    funcObj={{}}
                                                                                />
                                                                                <div className="mt-10px mb-10px">
                                                                                    <div>{question?.type_question_desc}</div>
                                                                                </div>
                                                                                {question?.categoryType?.includes("Text") && (
                                                                                    <TextEditor
                                                                                        value={ans?.answer?.answer_text ?? ''}
                                                                                        autoFocus={false}
                                                                                        disabled={true}
                                                                                        from="shareAssignment"
                                                                                    />
                                                                                )}
                                                                                {ans?.answer?.answer_audio ? (
                                                                                    <ShareAssignmentAudioPlayer
                                                                                        url={ans?.answer?.answer_audio}
                                                                                        audioKey={`photoCollage-${index}-answer-audio`}
                                                                                        audioPlayersRef={audioPlayersRef}
                                                                                        pauseAllOtherAudioPlayers={pauseAllOtherAudioPlayers}
                                                                                        withTranscript={false}
                                                                                    />
                                                                                ) : (
                                                                                    <>
                                                                                        {question?.categoryType?.includes("Audio") && (
                                                                                            <AudioRecorder
                                                                                                maxTime={120}
                                                                                                handleAudio={() => { }}
                                                                                                refresh={() => { }}
                                                                                                isPreview={true}
                                                                                                source={ans?.answer?.answer_audio || ""}
                                                                                                onRegisterMediaElement={registerMediaElement}
                                                                                                onMediaPlay={handleMediaPlay}
                                                                                            />
                                                                                        )}
                                                                                    </>
                                                                                )}

                                                                            </>
                                                                        )}
                                                                    </Content>
                                                                </QuestionContainer>
                                                            </RelativeContainer>
                                                        );
                                                    })}
                                                </InnerWrapper>
                                            </AssignmentPreviewTop>
                                        </>
                                    )}
                                </Container>
                            </Wrapper>
                        </div>
                    </ResizeAndDraggable>
                </div>
            </Container>
        </SentryWrap>
    );
};

export default React.memo(ShareAssignmentWidget);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  // min-height: 500px;
  // background-color: rgb(58, 58, 58);
  position: fixed;
  top: 0;
  left: 0;
//   height: 100vh;
  padding-top: 0px;
  z-index: 99999999;
  cursor: grab;
  // left: 25%;
  //   margin: 15px auto;/
//   transform: translate(31%, 0%);

  @media (max-width: 1450px) {
    // width: 96%;
    transform: translate(0%, 0%);
  }
  @media (max-width: 992px) {
    // width: 95%;
    transform: translate(0%, 0%);
  }
`;